import React from 'react';
import { useLanguage } from './LanguageContext';
import { translations } from '../data/translations';
import { Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';

export const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Youtube, href: '#', label: 'YouTube' }
  ];

  const divisions = [
    t.services.carRental.title,
    t.services.chauffeur.title,
    t.services.tours.title,
    t.services.usedCars.title,
    t.services.construction.title,
    t.services.realEstate.title
  ];

  return (
    <footer className="bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <div className="flex items-center gap-3 mb-4">
              <div className="relative p-2 bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl border border-teal-500/30 backdrop-blur-sm">
                <img 
                  src="/rafan-logo.png" 
                  alt="Rafan Group" 
                  className="w-14 h-14 object-contain drop-shadow-[0_0_8px_rgba(20,184,166,0.5)]"
                />
                <div className="absolute inset-0 bg-teal-500/5 rounded-xl"></div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white tracking-wide">Rafan Group</h3>
                <p className="text-xs text-teal-400 font-medium">{t.footer.tagline}</p>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed">
              {language === 'en'
                ? 'A premier Qatari holding company delivering excellence across multiple sectors.'
                : 'شركة قطرية رائدة تقدم التميز في قطاعات متعددة.'}
            </p>
          </div>

          {/* Divisions */}
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h4 className="text-white font-semibold mb-4">{t.footer.divisions}</h4>
            <ul className="space-y-2">
              {divisions.map((division, index) => (
                <li key={index}>
                  <button className="text-gray-400 hover:text-teal-400 transition-colors duration-300">
                    {division}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Links */}
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h4 className="text-white font-semibold mb-4">
              {language === 'en' ? 'Follow Us' : 'تابعنا'}
            </h4>
            <div className={`flex gap-3 ${isRTL ? 'justify-end' : 'justify-start'}`}>
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/10 hover:bg-teal-500 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 hover:scale-110"
                    aria-label={social.label}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
            <div className="mt-6">
              <p className="text-gray-400 text-sm">
                {language === 'en' ? 'Email:' : 'البريد:'} <a href="mailto:info@rafangroup.com" className="text-teal-400 hover:underline">info@rafangroup.com</a>
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {language === 'en' ? 'Phone:' : 'الهاتف:'} <a href="tel:+97444115510" className="text-teal-400 hover:underline">+974 4411 5510</a>
              </p>
              <p className="text-gray-400 text-sm mt-1">
                <a href="tel:+97444303317" className="text-teal-400 hover:underline">+974 4430 3317</a>
              </p>
              <p className="text-gray-400 text-sm mt-1">
                <a href="tel:+97459994112" className="text-teal-400 hover:underline">+974 5999 4112</a>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Rafan Group. {t.footer.rights}
            </p>
            <div className="flex gap-6">
              <button className="text-gray-400 hover:text-teal-400 text-sm transition-colors duration-300">
                {language === 'en' ? 'Privacy Policy' : 'سياسة الخصوصية'}
              </button>
              <button className="text-gray-400 hover:text-teal-400 text-sm transition-colors duration-300">
                {language === 'en' ? 'Terms of Service' : 'شروط الخدمة'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};